import { useRef, useCallback, useEffect, useMemo } from 'react';
import { eventBus } from '@atc/cai-event-bus';
import { UDS_OPEN_BOA_CREDIT_APP_COMPLETED, debugWallet, UDS_OPEN_BOA_CREDIT_APP_REQUEST } from 'wallet-manager';
import { validateDealerInfo, validateVehicleInfo, validatePaymentInfo } from '../helpers/boaFormValidators.js';

/**
 * Custom hook for handling Bank of America credit application submission using declarative form
 * This is an alternative approach to test against the programmatic form creation
 * @param {string} dealerId - Dealer ID
 * @param {Object} vehicleInfo - Vehicle-related information
 * @param {Object} paymentsInfo - Payment-related information
 * @returns {Object} Hook result with formData and formRef
 */
function useBOACreditApp(dealerId, vehicleInfo, paymentsInfo, tenant) {
  const formRef = useRef(null);

  // Early return for non-BOA tenants to avoid unnecessary processing
  const isBoaTenant = tenant?.toLowerCase() === 'boa';

  /**
   * Extracts and processes data from dealerId, vehicleInfo, and paymentsInfo
   * @returns {Object} Processed data for BoA credit application
   */
  const processDataForBoaCreditApp = useCallback(() => {
    // Skip expensive processing for non-BOA tenants
    if (!isBoaTenant) {
      return {
        dealerInfo: {},
        paymentInfo: {},
        vehicleInfo: {}
      };
    }
    const {
      chromeStyleId = '',
      exteriorColor = '',
      images = [],
      interiorColor = '',
      isCertified = false,
      make = '',
      mileage = 0,
      model = '',
      owner = {},
      stockNumber = '',
      trim = '',
      type: vehicleCondition = 'used',
      vin = '',
      year = ''
    } = vehicleInfo || {};
    const {
      address = {},
      name = '',
      phone: phoneNumber = ''
    } = owner;
    const {
      city = '',
      state = '',
      street: addressLine1 = '',
      zip: zipCode = ''
    } = address;
    const {
      activeTab,
      dueAtSigningValue: dueAtSign = 0,
      financeDownPayment = 0,
      financeOffersObj = {},
      leaseDownPayment = 0,
      obfuscatedCredit: creditTier = '',
      totalEquity: netTrade = 0
    } = paymentsInfo || {};
    const {
      incentives = {},
      interestRate: apr = 0,
      loanTerm: term = 0,
      payment: estimatedPayment = 0,
      pricingDetails = {},
      totalOfPayments: loanAmount = 0
    } = financeOffersObj || {};
    const {
      msrp = 0,
      salePrice: price = 0
    } = pricingDetails || {};
    const {
      totalAmount: rebate = 0
    } = incentives;
    const isFinance = activeTab === 'finance';
    const formData = {
      dealerInfo: {
        addressLine1,
        // required field, but any string will do
        city,
        // required field, but any string will do
        dealerId,
        // required field, but any string will do
        name: name ?? vehicleInfo?.ownerName ?? 'Unknown',
        // required field, but any string will do
        phoneNumber,
        // required field, but any string will do
        state,
        // required field, valid US state code
        zipCode // required field, valid US zip code
      },
      paymentInfo: {
        apr,
        // required field
        creditTier,
        // optional field
        downPayment: isFinance ? financeDownPayment : leaseDownPayment,
        // required field
        dueAtSign,
        // optional field
        estimatedPayment,
        // optional field
        loanAmount,
        // required field
        msrp,
        // optional field
        netTrade,
        // optional field
        price,
        // required field
        rebate,
        // optional field
        selfReportedCredit: 0,
        // optional field, but will break form if not a number/number string
        term // optional field
      },
      vehicleInfo: {
        certifiedUsed: isCertified ? 'Y' : 'N',
        // optional field
        chromeStyleId,
        // optional field
        exteriorColor,
        // optional field
        interiorColor,
        // optional field
        make,
        // required field
        mileage,
        // required field
        model,
        // required field
        photoUrl: images?.[0] || images?.sources?.[0]?.url || '',
        // required field but any string will do
        stockNumber,
        // optional field
        trim,
        // required field
        vehicleCondition,
        // required field
        vin,
        // required field
        year // required field
      }
    };
    return formData;
  }, [dealerId, isBoaTenant, vehicleInfo, paymentsInfo]);

  /**
   * Validates payload data for BoA credit application
   * @param {Object} payload - The payload data to validate
   * @returns {Object} Validation result with isValid flag and errors
   */
  const validatePayload = useCallback(function () {
    let formData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const {
      dealerInfo = {},
      vehicleInfo: processedVehicleInfo = {},
      paymentInfo = {}
    } = formData;

    // Validate each section separately to reduce cognitive complexity
    const dealerValidation = validateDealerInfo(dealerInfo, dealerId);
    const vehicleValidation = validateVehicleInfo(processedVehicleInfo);
    const paymentValidation = validatePaymentInfo(paymentInfo);

    // Combine all errors and coerced data
    const allErrors = [...dealerValidation.errors, ...vehicleValidation.errors, ...paymentValidation.errors];
    const allCoercedData = {
      ...dealerValidation.coerced,
      ...vehicleValidation.coerced,
      ...paymentValidation.coerced
    };
    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      data: allCoercedData
    };
  }, [dealerId]);

  /**
   * Submits the form using the ref
   */
  const submitForm = useCallback(() => {
    if (formRef.current && typeof formRef.current.submit === 'function') {
      formRef.current.submit();
      return true;
    }
    return false;
  }, []);

  /**
   * Handles BoA credit application submission using the declarative form
   * @param {Object} eventData - Event data (unused in this implementation)
   */
  const handleBoaCreditAppRequest = useCallback(eventData => {
    try {
      // Process current data from the provided parameters
      const currentData = processDataForBoaCreditApp();

      // Validate the payload (but submit form regardless of validation status)
      const validation = validatePayload(currentData);

      // Always attempt to submit the form - let BoA handle validation errors on their end
      const submitSuccess = submitForm();
      if (submitSuccess) {
        // Emit success event (form was submitted, even if validation had issues)
        eventBus.emit(UDS_OPEN_BOA_CREDIT_APP_COMPLETED, {
          success: true,
          data: currentData,
          validationWarnings: validation.isValid ? null : validation.errors
        });
        debugWallet('UDS', 'BoA', 'uds:openBOACreditApp:completed');
      } else {
        eventBus.emit(UDS_OPEN_BOA_CREDIT_APP_COMPLETED, {
          success: false,
          error: 'Form reference not available for submission',
          validationWarnings: validation.isValid ? null : validation.errors
        });
        debugWallet('UDS', 'BoA', 'uds:openBOACreditApp:completed');
      }
    } catch (error) {
      debugWallet('UDS', 'BoA', 'Error processing the credit application request:', error);
      eventBus.emit(UDS_OPEN_BOA_CREDIT_APP_COMPLETED, {
        success: false,
        error: error.message
      });
      debugWallet('UDS', 'BoA', 'uds:openBOACreditApp:completed');
    }
  }, [processDataForBoaCreditApp, validatePayload, submitForm]);

  // Set up event listeners
  useEffect(() => {
    // Setup conditionally
    if (isBoaTenant) {
      eventBus.on(UDS_OPEN_BOA_CREDIT_APP_REQUEST, handleBoaCreditAppRequest);
    }

    // Always return cleanup function at the useEffect level
    return () => {
      eventBus.off(UDS_OPEN_BOA_CREDIT_APP_REQUEST, handleBoaCreditAppRequest);
    };
  }, [handleBoaCreditAppRequest, isBoaTenant]);

  // Memoize the form data to avoid calling processDataForBoaCreditApp on every render
  const formData = useMemo(() => {
    // Only process data for BOA tenants
    if (!isBoaTenant) {
      return undefined;
    }
    try {
      return processDataForBoaCreditApp();
    } catch (error) {
      debugWallet('UDS', 'BoA', 'Error processing credit application form data:', error);
      // Return empty object to prevent breaking the component
      return {
        dealerInfo: {},
        paymentInfo: {},
        vehicleInfo: {}
      };
    }
  }, [processDataForBoaCreditApp, isBoaTenant]);

  // Only return data for BOA tenants
  if (isBoaTenant) {
    return {
      formData,
      formRef,
      submitForm
    };
  }
  return undefined;
}
export { useBOACreditApp as default };
